import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Zap, Sun, Moon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/courses", label: "Courses" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0D0F12]/80 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <div className="relative w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden group-hover:ring-2 group-hover:ring-amber-400/40 transition">
            <img src="/aducation-logo.png" alt="Aducation" className="w-11 h-11 object-cover rounded-full" />
          </div>
          <div className="leading-none">
            <div className="font-display font-extrabold text-xl tracking-tight text-white">
              Aducation
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "text-white bg-white/5"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            data-testid="nav-theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="w-10 h-10 rounded-xl border border-white/10 flex items-center justify-center text-slate-300 hover:text-amber-400 hover:border-amber-400/40 transition"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {user && user.role === "admin" ? (
            <>
              <Link
                to="/admin"
                data-testid="nav-admin-dashboard"
                className="text-sm font-medium text-amber-400 px-3 py-2 hover:text-amber-300"
              >
                Dashboard
              </Link>
              <button
                data-testid="nav-logout-btn"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5"
              >
                <LogOut size={16} /> Logout
              </button>
            </>
          ) : null}
          <Link
            to="/#download"
            data-testid="nav-download-cta"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white btn-red-glow hover:brightness-110 transition"
          >
            <Zap size={16} /> Download App
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-1">
          <button
            data-testid="nav-theme-toggle-mobile"
            onClick={toggle}
            aria-label="Toggle theme"
            className="w-10 h-10 rounded-xl border border-white/10 flex items-center justify-center text-slate-300 hover:text-amber-400 transition"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-white p-2"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#0D0F12] border-t border-white/10 px-4 py-4 space-y-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive ? "text-white bg-white/5" : "text-slate-300 hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user && user.role === "admin" ? (
            <>
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="block px-4 py-3 rounded-lg text-sm text-amber-400"
              >
                Admin Dashboard
              </Link>
              <button
                onClick={() => {
                  logout();
                  setOpen(false);
                  navigate("/");
                }}
                className="block w-full text-left px-4 py-3 rounded-lg text-sm text-slate-300"
              >
                Logout
              </button>
            </>
          ) : null}
        </div>
      )}
    </header>
  );
}
